import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import { useStores } from '../context/StoresContext';
import { getManufacturers } from '../services/manufacturersApi';
import { getProducts } from '../services/productsApi';
import {
  getCalculations,
  createCalculation,
  updateCalculation,
  deleteCalculation,
} from '../services/calculationsApi';

const FORMULA_VARS = [
  { label: 'MFG Price', value: 'mfg_price', desc: 'Pricing row manufacturer price' },
  { label: 'Add On', value: 'add_on', desc: 'Pricing row add-on (added after multiplier)' },
  { label: 'Conv Rate', value: 'conv_rate', desc: 'Manufacturer currency conversion rate' },
  { label: 'Discount', value: 'discount', desc: 'Manufacturer discount rate' },
  { label: 'Tariff', value: 'tariff', desc: 'Country tariff rate' },
  { label: 'Markup', value: 'markup', desc: 'Manufacturer markup rate' },
];

const Calculations = () => {
  const { activeStores } = useStores();
  const [items, setItems] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [formStoreId, setFormStoreId] = useState('');
  const [formMfgIds, setFormMfgIds] = useState([]);
  const [formFormula, setFormFormula] = useState('');
  const [formScope, setFormScope] = useState('all'); // 'all' | 'manufacturer' | 'product'
  const [formProductIds, setFormProductIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [calcsData, mfgData, prodsData] = await Promise.all([
        getCalculations(),
        getManufacturers(),
        getProducts(1, 500),
      ]);
      setItems(calcsData.items);
      setManufacturers(mfgData.items || []);
      setAllProducts(prodsData.products || []);
    } catch (err) {
      console.error('Failed to fetch calculations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setFormName('');
    setFormStoreId('');
    setFormMfgIds([]);
    setFormFormula('');
    setFormScope('all');
    setFormProductIds([]);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormName(item.name);
    setFormStoreId(item.store_id || '');
    const mfgs = item.manufacturers || [];
    setFormMfgIds(mfgs.map((m) => m.id));
    setFormFormula(item.formula || '');
    const pids = item.product_ids || [];
    if (pids.length > 0) setFormScope('product');
    else if (mfgs.length > 0 || item.manufacturer_id) setFormScope('manufacturer');
    else setFormScope('all');
    setFormProductIds(pids);
    setError('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditItem(null);
    setError('');
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setError('Name is required');
      return;
    }
    if (!formFormula.trim()) {
      setError('Formula is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const params = {
        name: formName.trim(),
        store_id: formStoreId || null,
        manufacturer_id: null,
        manufacturer_ids: formScope === 'manufacturer' ? formMfgIds : [],
        formula: formFormula.trim(),
        product_ids: formScope === 'product' ? formProductIds : [],
      };
      if (editItem) {
        await updateCalculation(editItem.id, params);
      } else {
        await createCalculation(params);
      }
      handleClose();
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this calculation? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await deleteCalculation(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  const insertVariable = (varName) => {
    setFormFormula((prev) => {
      const trimmed = prev.trimEnd();
      if (trimmed && !trimmed.endsWith(' ') && !/[+\-*/( ]$/.test(trimmed)) {
        return trimmed + ' ' + varName;
      }
      return trimmed + (trimmed ? ' ' : '') + varName;
    });
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Calculations" subtitle="Define pricing formulas for list price computation" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Calculations"
        subtitle="Define pricing formulas for list price computation"
        action={
          <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
            Add Calculation
          </Button>
        }
      />

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CalculateOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No calculations added
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Calculation" to create your first pricing formula
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Store</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Target</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 300 }}>Formula</TableCell>
                  <TableCell align="center" sx={{ width: 90 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const pids = item.product_ids || [];
                  const mfgs = item.manufacturers || [];
                  const scope = pids.length > 0 ? 'product' : (mfgs.length > 0 || item.manufacturer_id ? 'manufacturer' : 'all');
                  const scopeColor = scope === 'product' ? 'primary' : scope === 'manufacturer' ? 'secondary' : 'default';
                  const targetText = scope === 'product'
                    ? `${pids.length} product${pids.length !== 1 ? 's' : ''}`
                    : scope === 'manufacturer'
                    ? (mfgs.length > 0 ? mfgs.map((m) => m.name).join(', ') : (item.manufacturer_name || '--'))
                    : 'All products';
                  return (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={scope} color={scopeColor} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {storeLabel(item) || 'All stores'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{targetText}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          bgcolor: 'grey.50',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                        }}
                      >
                        {item.formula}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'primary.main' }}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item.id)}
                            disabled={deleting === item.id}
                            sx={{ color: 'error.main' }}
                          >
                            {deleting === item.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DeleteOutlineRounded fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editItem ? 'Edit Calculation' : 'Add Calculation'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            placeholder="e.g. US List Price"
            value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            error={!!error && !formName.trim()}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Store</InputLabel>
              <Select value={formStoreId} onChange={(e) => setFormStoreId(e.target.value)} label="Store">
                <MenuItem value=""><em>All Stores</em></MenuItem>
                {activeStores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {storeLabel(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select value={formScope} onChange={(e) => setFormScope(e.target.value)} label="Scope">
                <MenuItem value="all">All Products</MenuItem>
                <MenuItem value="manufacturer">Manufacturer-specific</MenuItem>
                <MenuItem value="product">Product-specific</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {formScope === 'manufacturer' && (
            <Autocomplete
              multiple
              size="small"
              options={manufacturers}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={manufacturers.filter((m) => formMfgIds.includes(m.id))}
              onChange={(_, val) => setFormMfgIds(val.map((v) => v.id))}
              renderInput={(params) => <TextField {...params} label="Manufacturers" placeholder="Select manufacturers…" />}
              sx={{ mb: 2 }}
            />
          )}

          {formScope === 'product' && (
            <Autocomplete
              multiple
              size="small"
              options={allProducts}
              getOptionLabel={(o) => o.title}
              value={allProducts.filter((p) => formProductIds.includes(p.id))}
              onChange={(_, val) => setFormProductIds(val.map((v) => v.id))}
              renderInput={(params) => <TextField {...params} label="Products" placeholder="Select products…" />}
              sx={{ mb: 2 }}
            />
          )}

          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Variables (click to insert)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {FORMULA_VARS.map((v) => (
              <Tooltip key={v.value} title={v.desc}>
                <Chip
                  label={v.label}
                  size="small"
                  variant="outlined"
                  onClick={() => insertVariable(v.value)}
                  sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </Tooltip>
            ))}
            {['+', '-', '*', '/', '(', ')'].map((op) => (
              <Chip
                key={op}
                label={op}
                size="small"
                color="default"
                onClick={() => insertVariable(op)}
                sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 700 }}
              />
            ))}
          </Box>

          <TextField
            label="Formula"
            placeholder="e.g. mfg_price * conv_rate * (1 - discount) * (1 + tariff) * (1 + markup)"
            value={formFormula}
            onChange={(e) => { setFormFormula(e.target.value); setError(''); }}
            fullWidth
            multiline
            rows={3}
            error={!!error && !formFormula.trim()}
            helperText={error || 'Use variables and math operators to build the formula'}
            sx={{
              '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : editItem ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calculations;
